import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import axios from 'axios';
import DynamicSelect from '../DynamicSelect';

const API_KEY = 'caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079';
const BAD_STOCK_API = 'https://api.muktihospital.com/api/bad-stock/add';
const MEDICINE_PURCHASE_API = 'https://api.muktihospital.com/api/medicine-purchase';

const AddBadStockModal = ({ isOpen, onClose, medicine, onBadStockSave }) => {
  const [batchNo, setBatchNo] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [outwardDate, setOutwardDate] = useState(new Date().toISOString().split('T')[0]);
  const [quantity, setQuantity] = useState('');
  const [note, setNote] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingBatches, setIsFetchingBatches] = useState(false);
  const [errors, setErrors] = useState({});
  const [batches, setBatches] = useState([]);
  const [fetchError, setFetchError] = useState(null);

  useEffect(() => {
    if (!isOpen || !medicine?.id) return;

    const fetchBatches = async () => {
      setIsFetchingBatches(true);
      setFetchError(null);
      try {
        // console.log('Fetching batches for medicineId:', medicine.id);
        const response = await axios.get(
          `${MEDICINE_PURCHASE_API}?medicineId=${medicine.id}`,
          { headers: { 'x-api-key': API_KEY } }
        );

        // console.log('Raw API response:', JSON.stringify(response.data, null, 2));

        let purchaseRecords = [];
        if (Array.isArray(response.data.purchases)) {
          purchaseRecords = response.data.purchases;
          // console.log('Using response.data.purchases as purchaseRecords');
        } else if (Array.isArray(response.data)) {
          purchaseRecords = response.data;
          // console.log('Using response.data as purchaseRecords');
        } else if (response.data?.items && Array.isArray(response.data.items)) {
          purchaseRecords = response.data.items;
          // console.log('Using response.data.items as purchaseRecords');
        } else {
          console.warn('Unexpected API response structure:', response.data);
          setFetchError('Invalid API response structure. Please contact support.');
          return;
        }

        // console.log('Purchase records:', JSON.stringify(purchaseRecords, null, 2));

        const allItems = purchaseRecords.flatMap(rec => {
          if (!rec || !rec.id) {
            console.warn('Invalid purchase record:', rec);
            return [];
          }

          let parsedItems = [];
          try {
            if (typeof rec.items === 'string') {
              parsedItems = JSON.parse(rec.items);
            } else if (Array.isArray(rec.items)) {
              parsedItems = rec.items;
            }
          } catch (e) {
            console.error("Error parsing items for purchase ID:", rec.id, e);
            parsedItems = [];
          }

          return parsedItems.map((item, index) => ({
            ...item,
            purchaseId: rec.id,
            itemIndex: index,
            quantity: parseInt(item.quantity || 0, 10),
            availableQuantity: parseInt(item.availableQuantity !== undefined ? item.availableQuantity : item.quantity, 10)
          }));
        });

        // console.log('Flattened items:', JSON.stringify(allItems, null, 2));

        const validBatches = allItems
          .filter(rec => {
            const batchNoStr = rec?.batchNo?.toString().trim();
            const isValidBatchNo = batchNoStr !== undefined && batchNoStr !== null && batchNoStr !== '';
            const rawQuantity = rec?.availableQuantity;
            const parsedQuantity = rawQuantity != null ? parseInt(rawQuantity, 10) : null;
            const isValidQuantity = parsedQuantity != null && !isNaN(parsedQuantity) && parsedQuantity >= 0;
            const parsedItemIndex = parseInt(rec?.itemIndex, 10);
            const isValidItemIndex = !isNaN(parsedItemIndex);
            const isMatchingMedicine =
              rec?.medicineId === medicine.id ||
              parseInt(rec?.medicineId, 10) === parseInt(medicine.id, 10);
            // console.log(
            //   `Filtering record:`,
            //   {
            //     batchNo: batchNoStr,
            //     isValidBatchNo,
            //     rawAvailableQuantity: rawQuantity,
            //     parsedQuantity,
            //     isValidQuantity,
            //     itemIndex: rec?.itemIndex,
            //     isValidItemIndex,
            //     medicineId: rec?.medicineId,
            //     expectedMedicineId: medicine.id,
            //     isMatchingMedicine,
            //     record: rec,
            //   }
            // );
            return isValidBatchNo && isValidQuantity && isValidItemIndex && isMatchingMedicine;
          })
          .map(rec => ({
            id: rec.purchaseId || rec.id || rec.medicineId,
            itemIndex: parseInt(rec.itemIndex, 10),
            batchNo: rec.batchNo.toString(),
            availableQuantity: parseInt(rec.availableQuantity, 10) || 0,
            expiryDate: rec.expiryDate
              ? new Date(rec.expiryDate).toISOString().split('T')[0]
              : '',
          }));

        // console.log('Valid batches:', JSON.stringify(validBatches, null, 2));

        setBatches(validBatches);
        if (!validBatches.length) {
          setFetchError(
            `No valid batches found for ${medicine.medicineName} (ID: ${medicine.id}). Please check if purchase records exist with valid batch details (batchNo, availableQuantity >= 0, medicineId: ${medicine.id}).`
          );
        } else if (!validBatches.some(b => b.availableQuantity > 0)) {
          setFetchError(
            `No batches with available quantity > 0 found for ${medicine.medicineName} (ID: ${medicine.id}). Please restock in the Purchase section.`
          );
        }
      } catch (err) {
        console.error('Batch fetch error:', err);
        console.error('Error response:', err.response?.data);
        const msg = err.response?.data?.error || 'Failed to fetch batch data. Please try again or contact support.';
        setFetchError(msg);
        toast.error(msg);
        setBatches([]);
      } finally {
        setIsFetchingBatches(false);
      }
    };

    fetchBatches();
  }, [isOpen, medicine]);

  if (!isOpen || !medicine) return null;

  const batchOptions = batches.length
    ? [
        { value: '', label: 'Select Batch' },
        ...batches.map(b => ({
          value: b.batchNo,
          label: `${b.batchNo} (Available Qty: ${b.availableQuantity}, ItemIndex: ${b.itemIndex})${b.availableQuantity === 0 ? ' [Out of Stock]' : ''}`,
          isDisabled: b.availableQuantity === 0,
        })),
      ]
    : [{ value: '', label: 'No batches available' }];

  const validateForm = () => {
    const newErrors = {};
    if (!batchNo) newErrors.batchNo = 'Batch number is required';
    if (!outwardDate) newErrors.outwardDate = 'Outward date is required';
    const parsedQty = parseInt(quantity, 10);
    if (!quantity || isNaN(parsedQty) || parsedQty <= 0) {
      newErrors.quantity = 'Valid quantity is required';
    } else {
      const selBatch = batches.find(b => b.batchNo === batchNo);
      if (selBatch) {
        if (parsedQty > selBatch.availableQuantity) {
          newErrors.quantity = `Quantity cannot exceed available quantity (${selBatch.availableQuantity})`;
        }
        const newAvailableQuantity = selBatch.availableQuantity - parsedQty;
        if (!Number.isInteger(newAvailableQuantity) || newAvailableQuantity < 0) {
          newErrors.quantity = 'Resulting available quantity cannot be negative or non-integer';
        }
        if (isNaN(parseInt(selBatch.itemIndex, 10))) {
          newErrors.batchNo = 'Selected batch has an invalid item index';
        }
      } else {
        newErrors.batchNo = 'Selected batch not found';
      }
    }
    setErrors(newErrors);
    return !Object.keys(newErrors).length;
  };

  const handleBatchNoChange = sel => {
    const val = sel?.value || '';
    // console.log('Selected batchNo:', val);
    setBatchNo(val);
    setErrors(prev => ({ ...prev, batchNo: '' }));
    const selBatch = batches.find(b => b.batchNo === val);
    setExpiryDate(selBatch?.expiryDate || '');
  };

  const handleQuantityChange = e => {
    const val = e.target.value;
    setQuantity(val);
    const parsedQty = parseInt(val, 10);
    const selBatch = batches.find(b => b.batchNo === batchNo);
    setErrors(prev => ({
      ...prev,
      quantity: val && !isNaN(parsedQty) && parsedQty > 0
        ? selBatch && parsedQty <= selBatch.availableQuantity
          ? ''
          : `Quantity cannot exceed available quantity (${selBatch?.availableQuantity || 0})`
        : 'Valid quantity is required',
    }));
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    setIsLoading(true);
    try {
      const selBatch = batches.find(b => b.batchNo === batchNo);
      if (!selBatch) {
        throw new Error('Selected batch not found');
      }
      const itemIndex = parseInt(selBatch.itemIndex, 10);
      if (isNaN(itemIndex)) {
        throw new Error('Invalid item index for selected batch');
      }

      const parsedQty = parseInt(quantity, 10);
      const currentAvailableQuantity = parseInt(selBatch.availableQuantity, 10) || 0;
      const newAvailableQuantity = currentAvailableQuantity - parsedQty;
      if (!Number.isInteger(newAvailableQuantity) || newAvailableQuantity < 0) {
        throw new Error('Invalid or negative available quantity');
      }

      const badStockPayload = {
        medicineId: medicine.id,
        medicineName: medicine.medicineName,
        batchNo,
        itemIndex,
        expiryDate,
        outwardDate,
        qty: parsedQty,
        note,
      };
      // console.log('Posting bad stock payload:', badStockPayload);
      await axios.post(BAD_STOCK_API, badStockPayload, {
        headers: {
          'x-api-key': API_KEY,
          'Content-Type': 'application/json',
        },
      });

      const patchPayload = {
        itemIndex,
        availableQuantity: newAvailableQuantity, // Changed from 'quantity' to 'availableQuantity'
      };
      // console.log('Patching payload:', {
      //   purchaseId: selBatch.id,
      //   itemIndex: patchPayload.itemIndex,
      //   availableQuantity: patchPayload.availableQuantity,
      // });
      const patchResponse = await axios.patch(
        `${MEDICINE_PURCHASE_API}/${selBatch.id}/available-quantity`,
        patchPayload,
        {
          headers: {
            'x-api-key': API_KEY,
            'Content-Type': 'application/json',
          },
        }
      );
      // console.log('PATCH response:', patchResponse.data);

      onBadStockSave(medicine.id);
      toast.success(`Bad stock added for ${medicine.medicineName}`);
      onClose();
    } catch (err) {
      console.error('Save error:', err);
      console.error('Error response:', err.response?.data || err.message);
      let msg = 'Failed to add bad stock';
      if (err.response?.data?.error) {
        msg = err.response.data.error;
        if (msg.includes('availableQuantity')) {
          setErrors(prev => ({
            ...prev,
            quantity: msg,
          }));
        }
      }
      toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setBatchNo('');
    setExpiryDate('');
    setOutwardDate(new Date().toISOString().split('T')[0]);
    setQuantity('');
    setNote('');
    setErrors({});
    setBatches([]);
    setFetchError(null);
    onClose();
  };

  const modalContent = (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 px-4 sm:px-0">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-full sm:max-w-[80%] md:max-w-[700px] lg:max-w-screen-lg max-h-[90vh] overflow-y-auto">
        <div className="p-4 sm:p-6 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-lg sm:text-xl font-bold text-gray-700">
            Add Bad Stock for {medicine.medicineName}
          </h2>
        </div>
        {fetchError && !isFetchingBatches && (
          <div className="px-5 py-2 text-red-500">{fetchError}</div>
        )}
        <div className="px-4 sm:px-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
          <div>
            <label className="label">
              Batch No <span className="text-red-500">*</span>
            </label>
            <DynamicSelect
              options={batchOptions}
              value={batchOptions.find(o => o.value === batchNo) || null}
              onChange={handleBatchNoChange}
              isSearchable
              isDisabled={isLoading || isFetchingBatches || !batches.length}
            />
            {errors.batchNo && (
              <span className="text-red-500 text-xs mt-1">{errors.batchNo}</span>
            )}
          </div>
          <div>
            <label className="label">Expiry Date</label>
            <input
              type="date"
              value={expiryDate}
              onChange={e => setExpiryDate(e.target.value)}
              className="input-field"
              disabled={isLoading || isFetchingBatches || !batches.length}
            />
          </div>
          <div>
            <label className="label">
              Outward Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={outwardDate}
              onChange={e => setOutwardDate(e.target.value)}
              className="input-field"
              disabled={isLoading || isFetchingBatches || !batches.length}
            />
            {errors.outwardDate && (
              <span className="text-red-500 text-xs mt-1">{errors.outwardDate}</span>
            )}
          </div>
          <div>
            <label className="label">
              Qty <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              value={quantity}
              onChange={handleQuantityChange}
              className={`input-field ${errors.quantity ? 'border-red-500' : ''}`}
              placeholder="Enter quantity"
              disabled={isLoading || isFetchingBatches || !batches.length || !batchNo}
            />
            {errors.quantity && (
              <span className="text-red-500 text-xs mt-1">{errors.quantity}</span>
            )}
          </div>
        </div>
        <div className="mb-4 p-4 sm:p-6">
          <label className="block text-sm font-medium text-gray-700 mb-1">Note</label>
          <textarea
            value={note}
            onChange={e => setNote(e.target.value)}
            className="input-field"
            rows="3"
            placeholder="Enter note"
            disabled={isLoading || isFetchingBatches || !batches.length}
          />
        </div>
        <div className="flex flex-col sm:flex-row justify-end gap-3 p-4 sm:p-6 border-t border-gray-200">
          <button
            onClick={handleClose}
            className="bg-[#E7633D]/90 text-sm text-white py-2 px-6 rounded-md hover:bg-[#E7633D] transition-all duration-300"
            disabled={isLoading || isFetchingBatches}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className={`bg-M-primary-color/90 text-sm text-white py-2 px-6 rounded-md transition-all duration-200 ${
              isLoading || !batches.length || !batchNo ? 'opacity-50 cursor-not-allowed' : 'hover:bg-M-primary-color'
            }`}
            disabled={isLoading || !batches.length || !batchNo}
          >
            {isLoading ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default AddBadStockModal;